// ===================================================================
// ⚙️ CONFIGURAÇÕES DA INSTITUIÇÃO
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';
import { WhatsAppIcon } from '../../components/icons/WhatsAppIcon';

const DEFAULT_LOGO = '/static/ingressa-logo.svg';
const MAX_LOGO_SIZE_BYTES = 10 * 1024 * 1024;

function maskCnpjInput(value) {
  const d = String(value || '').replace(/\D/g, '').slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

function maskPhoneInput(value) {
  const n = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (n.length <= 2) return n.length ? `(${n}` : '';
  if (n.length <= 6) return `(${n.slice(0, 2)}) ${n.slice(2)}`;
  if (n.length <= 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
  return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
}
const SESSION_IDLE_MIN = 5;
const SESSION_IDLE_MAX = 60;
const SESSION_IDLE_DEFAULT = 15;

const EyeToggle = ({ visible, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    tabIndex={-1}
    style={{
      position: 'absolute',
      right: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px',
      color: '#6B7280',
    }}
    title={visible ? 'Ocultar senha' : 'Ver senha'}
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {visible ? (
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      ) : (
        <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
      )}
    </svg>
  </button>
);

export const ConfigManager = React.memo(({ isMaster, onSaved }) => {
  const [config, setConfig] = useState({ institution_name: '', system_name: 'Ingressa', institution_icon: '', institution_icon_right: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [backupList, setBackupList] = useState([]);
  const [backupConfig, setBackupConfig] = useState({ backup_manter: 10, backup_frequencia: '' });
  const [backupConfigSaving, setBackupConfigSaving] = useState(false);
  const [mailConfig, setMailConfig] = useState({
    mail_server: '',
    mail_port: 587,
    mail_use_tls: true,
    mail_username: '',
    mail_password: '',
    mail_default_sender: '',
    mail_contact_message: '',
    mail_contact_message_default: '',
    mail_configured: false,
  });
  const [mailConfigSaving, setMailConfigSaving] = useState(false);
  const [showMailPassword, setShowMailPassword] = useState(false);
  const [cadunicoConfig, setCadunicoConfig] = useState({
    cadunico_enabled: false,
    cadunico_client_id: '',
    cadunico_client_secret: '',
    cadunico_token_url: '',
    cadunico_api_url: '',
    cadunico_environment: 'homolog',
    cadunico_use_stub: true,
    cadunico_configured: false,
    cadunico_ready: false,
  });
  const [cadunicoSaving, setCadunicoSaving] = useState(false);
  const [showCadunicoSecret, setShowCadunicoSecret] = useState(false);
  const [showCadunicoAdvanced, setShowCadunicoAdvanced] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [previewError, setPreviewError] = useState({ left: false, right: false, owner: false, system: false });
  const [formData, setFormData] = useState({
    institution_name: '',
    system_name: '',
    institution_icon: '',
    institution_icon_right: '',
    platform_owner_legal_name: '',
    platform_owner_cnpj: '',
    platform_owner_phone: '',
    platform_owner_phone_whatsapp: false,
    platform_owner_icon: '',
    terms_of_use: '',
    session_idle_timeout_minutes: SESSION_IDLE_DEFAULT,
  });
  const { addNotification } = useNotifications();
  const validateLogoFile = useCallback((file) => {
    if (!file) return 'Nenhum arquivo selecionado.';
    const type = (file.type || '').toLowerCase();
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(type)) {
      return 'Formato inválido. Use apenas JPG, JPEG ou PNG.';
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      return 'Arquivo muito grande. Tamanho máximo: 10MB.';
    }
    return '';
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getConfig();
      setConfig(data);
      setFormData({
        institution_name: data.institution_name ?? '',
        system_name: data.system_name || 'Ingressa',
        institution_icon: data.institution_icon || '',
        institution_icon_right: data.institution_icon_right || '',
        platform_owner_legal_name: data.platform_owner_legal_name || '',
        platform_owner_cnpj: maskCnpjInput(data.platform_owner_cnpj || ''),
        platform_owner_phone: maskPhoneInput(data.platform_owner_phone || ''),
        platform_owner_phone_whatsapp:
          data.platform_owner_phone_whatsapp === '1' || data.platform_owner_phone_whatsapp === true,
        platform_owner_icon: data.platform_owner_icon || '',
        terms_of_use: data.terms_of_use || '',
        session_idle_timeout_minutes: Number.isFinite(Number(data.session_idle_timeout_minutes))
          ? Number(data.session_idle_timeout_minutes)
          : SESSION_IDLE_DEFAULT,
      });
      setPreviewError({ left: false, right: false, owner: false, system: false });
    } catch (err) {
      setError('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const loadBackupData = useCallback(async () => {
    if (isMaster) return;
    try {
      const [list, cfg] = await Promise.all([apiClient.getBackupList(), apiClient.getBackupConfig()]);
      setBackupList(list || []);
      setBackupConfig({ backup_manter: cfg?.backup_manter ?? 10, backup_frequencia: cfg?.backup_frequencia || '' });
    } catch {
      setBackupList([]);
    }
  }, [isMaster]);

  useEffect(() => {
    if (!isMaster) loadBackupData();
  }, [isMaster, loadBackupData]);

  const loadMailConfig = useCallback(async () => {
    if (isMaster) return;
    try {
      const data = await apiClient.getConfigMail();
      setMailConfig({
        mail_server: data.mail_server || '',
        mail_port: data.mail_port ?? 587,
        mail_use_tls: data.mail_use_tls !== false,
        mail_username: data.mail_username || '',
        mail_password: '',
        mail_default_sender: data.mail_default_sender || '',
        mail_contact_message: data.mail_contact_message || data.mail_contact_message_default || '',
        mail_contact_message_default: data.mail_contact_message_default || 'Em caso de dúvidas, entre em contato com o setor de processos seletivos da instituição.',
        mail_configured: Boolean(data.mail_configured),
      });
    } catch {
      const defaultContact = 'Em caso de dúvidas, entre em contato com o setor de processos seletivos da instituição.';
      setMailConfig({
        mail_server: '',
        mail_port: 587,
        mail_use_tls: true,
        mail_username: '',
        mail_password: '',
        mail_default_sender: '',
        mail_contact_message: defaultContact,
        mail_contact_message_default: defaultContact,
        mail_configured: false,
      });
    }
  }, [isMaster]);

  useEffect(() => {
    if (!isMaster) loadMailConfig();
  }, [isMaster, loadMailConfig]);

  const loadCadunicoConfig = useCallback(async () => {
    if (isMaster) return;
    try {
      const data = await apiClient.getConfigCadunico();
      setCadunicoConfig({
        cadunico_enabled: Boolean(data.cadunico_enabled),
        cadunico_client_id: data.cadunico_client_id || '',
        cadunico_client_secret: '',
        cadunico_token_url: data.cadunico_token_url || '',
        cadunico_api_url: data.cadunico_api_url || '',
        cadunico_environment: data.cadunico_environment || 'homolog',
        cadunico_use_stub: Boolean(data.cadunico_use_stub),
        cadunico_configured: Boolean(data.cadunico_configured || data.cadunico_client_secret_set),
        cadunico_ready: Boolean(data.cadunico_ready),
      });
    } catch {
      setCadunicoConfig({
        cadunico_enabled: false,
        cadunico_client_id: '',
        cadunico_client_secret: '',
        cadunico_token_url: '',
        cadunico_api_url: '',
        cadunico_environment: 'homolog',
        cadunico_use_stub: true,
        cadunico_configured: false,
        cadunico_ready: false,
      });
    }
  }, [isMaster]);

  useEffect(() => {
    if (!isMaster) loadCadunicoConfig();
  }, [isMaster, loadCadunicoConfig]);

  const handleSaveCadunicoConfig = useCallback(async () => {
    if (isMaster) return;
    setCadunicoSaving(true);
    try {
      const toSend = {
        cadunico_enabled: cadunicoConfig.cadunico_enabled,
        cadunico_client_id: cadunicoConfig.cadunico_client_id,
        cadunico_token_url: cadunicoConfig.cadunico_token_url,
        cadunico_api_url: cadunicoConfig.cadunico_api_url,
        cadunico_environment: cadunicoConfig.cadunico_environment,
        cadunico_use_stub: cadunicoConfig.cadunico_use_stub,
      };
      if (cadunicoConfig.cadunico_client_secret) {
        toSend.cadunico_client_secret = cadunicoConfig.cadunico_client_secret;
      }
      await apiClient.updateConfig(toSend);
      addNotification('Sucesso', 'Configurações CadÚnico salvas.', 'success');
      await loadCadunicoConfig();
    } catch (err) {
      addNotification('Erro', err.message || 'Erro ao salvar CadÚnico', 'error');
    } finally {
      setCadunicoSaving(false);
    }
  }, [isMaster, cadunicoConfig, addNotification, loadCadunicoConfig]);


  const handleSaveMailConfig = useCallback(async () => {
    if (isMaster) return;
    setMailConfigSaving(true);
    try {
      const toSend = { ...mailConfig };
      if (!toSend.mail_password) delete toSend.mail_password;
      delete toSend.mail_contact_message_default;
      delete toSend.mail_configured;
      await apiClient.updateConfig(toSend);
      addNotification('Sucesso', 'Configurações de e-mail salvas.', 'success');
      await loadMailConfig();
    } catch (err) {
      addNotification('Erro', err.message || 'Erro ao salvar', 'error');
    } finally {
      setMailConfigSaving(false);
    }
  }, [isMaster, mailConfig, addNotification, loadMailConfig]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      let toSend;
      if (isMaster) {
        toSend = {
          platform_owner_legal_name: (formData.platform_owner_legal_name || '').trim(),
          platform_owner_cnpj: formData.platform_owner_cnpj,
          platform_owner_phone: formData.platform_owner_phone,
          platform_owner_phone_whatsapp: formData.platform_owner_phone_whatsapp,
          platform_owner_icon: formData.platform_owner_icon,
          institution_name: (formData.institution_name || '').trim(),
          system_name: (formData.system_name || '').trim(),
          institution_icon: formData.institution_icon,
        };
      } else {
        const timeoutMinutes = Number(formData.session_idle_timeout_minutes);
        if (!Number.isInteger(timeoutMinutes) || timeoutMinutes < SESSION_IDLE_MIN || timeoutMinutes > SESSION_IDLE_MAX) {
          setError(`Tempo de inatividade deve estar entre ${SESSION_IDLE_MIN} e ${SESSION_IDLE_MAX} minutos.`);
          setSaving(false);
          return;
        }
        toSend = { ...formData, session_idle_timeout_minutes: timeoutMinutes };
      }
      const saved = await apiClient.updateConfig(toSend);
      setConfig(saved);
      setFormData((prev) => ({
        ...prev,
        ...saved,
        platform_owner_cnpj: maskCnpjInput(saved.platform_owner_cnpj || prev.platform_owner_cnpj || ''),
        platform_owner_phone: maskPhoneInput(saved.platform_owner_phone || prev.platform_owner_phone || ''),
        platform_owner_phone_whatsapp:
          saved.platform_owner_phone_whatsapp === '1' || saved.platform_owner_phone_whatsapp === true,
      }));
      const savedMsg = isMaster
        ? 'Alteração salva com sucesso!'
        : 'Configurações salvas com sucesso!';
      setSuccessMessage(savedMsg);
      addNotification('Sucesso', savedMsg, 'success');
      if (typeof onSaved === 'function') {
        window.setTimeout(() => onSaved(), 1100);
      }
    } catch (err) {
      setError(err.message || 'Erro ao salvar');
      setSuccessMessage('');
    } finally {
      setSaving(false);
    }
  }, [formData, addNotification, isMaster, onSaved]);

  const handleLogoUpload = useCallback(async (file, side) => {
    const validationError = validateLogoFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    const field = side === 'right' ? 'institution_icon_right' : 'institution_icon';
    const previewKey = side === 'right' ? 'right' : (isMaster ? 'system' : 'left');
    try {
      const url = await apiClient.uploadIcon(file);
      setFormData(prev => ({ ...prev, [field]: url }));
      setPreviewError(prev => ({ ...prev, [previewKey]: false }));
      await apiClient.updateConfig({ [field]: url });
      addNotification('Sucesso', `Imagem ${side === 'right' ? 'direita' : 'esquerda'} enviada e salva.`, 'success');
    } catch (err) {
      setError(err.message || 'Erro ao enviar imagem');
    }
  }, [addNotification, validateLogoFile, isMaster]);

  const handleRemoveIcon = useCallback(async (field) => {
    const previewKey = field === 'institution_icon_right'
      ? 'right'
      : field === 'institution_icon'
        ? (isMaster ? 'system' : 'left')
        : 'owner';
    setFormData((prev) => ({ ...prev, [field]: '' }));
    setPreviewError((prev) => ({ ...prev, [previewKey]: false }));
    try {
      await apiClient.updateConfig({ [field]: '' });
      addNotification('Sucesso', 'Imagem removida.', 'success');
    } catch (err) {
      setError(err.message || 'Erro ao remover imagem');
    }
  }, [addNotification, isMaster]);

  const renderImageActions = (field, previewKey, hasImage) => (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
      <label style={{ ...styles.button, ...styles.buttonSecondary, cursor: 'pointer', marginBottom: 0 }}>
        Buscar imagem
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          style={{ display: 'none' }}
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            const side = field === 'institution_icon_right' ? 'right' : field === 'institution_icon' ? 'left' : 'owner';
            if (side === 'owner') {
              const validationError = validateLogoFile(f);
              if (validationError) {
                setError(validationError);
                return;
              }
              try {
                const url = await apiClient.uploadIcon(f);
                setFormData((prev) => ({ ...prev, platform_owner_icon: url }));
                setPreviewError((prev) => ({ ...prev, owner: false }));
                await apiClient.updateConfig({ platform_owner_icon: url });
                addNotification('Sucesso', 'Ícone do proprietário enviado e salvo.', 'success');
              } catch (err) {
                setError(err.message || 'Erro ao enviar imagem');
              }
            } else {
              await handleLogoUpload(f, side);
            }
            e.target.value = '';
          }}
        />
      </label>
      {hasImage && (
        <button
          type="button"
          onClick={() => handleRemoveIcon(field)}
          style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 12px', fontSize: 12, color: '#B91C1C', borderColor: '#FECACA' }}
        >
          Remover imagem
        </button>
      )}
    </div>
  );

  const handleGenerateBackup = useCallback(async () => {
    if (isMaster) return;
    setBackupLoading(true);
    setError('');
    try {
      const data = await apiClient.generateBackup();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = data?.metadata?.institution_slug || 'instituicao';
      a.download = `ingressa_backup_${slug}_${new Date().toISOString().slice(0, 10)}_${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      addNotification('Sucesso', 'Backup gerado e baixado. Também salvo no servidor.', 'success');
      loadBackupData();
    } catch (err) {
      setError(err.message || 'Erro ao gerar backup');
      addNotification('Erro', err.message || 'Erro ao gerar backup', 'error');
    } finally {
      setBackupLoading(false);
    }
  }, [isMaster, addNotification, loadBackupData]);

  const handleRestoreBackup = useCallback(async () => {
    if (isMaster || !restoreFile) return;
    if (!window.confirm('Restaurar substituirá TODOS os dados atuais da instituição pelos do backup. Continuar?')) return;
    setRestoreLoading(true);
    setError('');
    try {
      await apiClient.restoreBackup(restoreFile);
      addNotification('Sucesso', 'Backup restaurado. Os usuários precisarão usar "Esqueci minha senha" para redefinir a senha.', 'success');
      setRestoreFile(null);
      document.getElementById('restore-file-input')?.value && (document.getElementById('restore-file-input').value = '');
      loadConfig();
      loadBackupData();
    } catch (err) {
      setError(err.message || 'Erro ao restaurar backup');
      addNotification('Erro', err.message || 'Erro ao restaurar', 'error');
    } finally {
      setRestoreLoading(false);
    }
  }, [isMaster, restoreFile, addNotification, loadConfig, loadBackupData]);

  const handleSaveBackupConfig = useCallback(async () => {
    if (isMaster) return;
    setBackupConfigSaving(true);
    try {
      await apiClient.updateBackupConfig(backupConfig);
      addNotification('Sucesso', 'Configurações de backup salvas.', 'success');
    } catch (err) {
      addNotification('Erro', err.message || 'Erro ao salvar', 'error');
    } finally {
      setBackupConfigSaving(false);
    }
  }, [isMaster, backupConfig, addNotification]);

  const formatBytes = (n) => {
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
    return (n / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('pt-BR');
    } catch {
      return iso;
    }
  };

  if (loading) return <LoadingSpinner text="Carregando configurações..." />;

  return (
    <div style={styles.contentContainer}>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>
            {isMaster ? '⚙️ Configurações — Empresa proprietária' : '⚙️ Configurações da Instituição'}
          </h2>
          {isMaster && (
            <p style={{ fontSize: '14px', color: '#6B7280', margin: '8px 0 0' }}>
              Cadastro da sua empresa (exibido na aba <strong>Sobre</strong> para todos os usuários). Clientes contratantes configuram o ambiente deles em suas próprias instituições.
            </p>
          )}
        </div>
        {successMessage && (
          <Alert message={successMessage} type="success" onClose={() => setSuccessMessage('')} />
        )}
        {error && <Alert message={error} type="error" onClose={() => setError('')} />}
        <form onSubmit={handleSubmit} style={styles.form}>
          {isMaster && (
            <>
              <div
                style={{
                  textAlign: 'center',
                  padding: '24px 20px',
                  marginBottom: 8,
                  backgroundColor: '#F9FAFB',
                  borderRadius: 12,
                  border: '1px solid #E5E7EB',
                }}
              >
                <label style={{ ...styles.label, display: 'block', marginBottom: 8 }}>
                  Logomarca da empresa proprietária
                </label>
                <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 20px', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
                  Envie PNG ou JPG (máx. 10 MB). Exibida no topo do card de login (centralizada) e como marca d&apos;água no fundo da tela.
                </p>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 180,
                    marginBottom: 16,
                  }}
                >
                  {(formData.platform_owner_icon?.startsWith('http') || formData.platform_owner_icon?.startsWith('/')) && !previewError.owner ? (
                    <img
                      key={formData.platform_owner_icon}
                      src={formData.platform_owner_icon}
                      alt=""
                      style={{ maxWidth: 200, maxHeight: 180, objectFit: 'contain', display: 'block' }}
                      onError={() => setPreviewError((prev) => ({ ...prev, owner: true }))}
                    />
                  ) : (
                    <span style={{ fontSize: 14, color: '#9CA3AF' }}>
                      {previewError.owner ? 'Erro ao carregar a imagem.' : 'Nenhuma logomarca cadastrada'}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  {renderImageActions(
                    'platform_owner_icon',
                    'owner',
                    (formData.platform_owner_icon?.startsWith('http') || formData.platform_owner_icon?.startsWith('/')) && !previewError.owner
                  )}
                </div>
              </div>

              <div style={{ paddingTop: 8, marginTop: 16, borderTop: '1px solid #E5E7EB' }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: '#374151', margin: '0 0 16px' }}>
                  Identidade do sistema (Ingressa)
                </h3>
              </div>
              <div>
                <label style={styles.label}>Sigla do sistema</label>
                <input
                  type="text"
                  value={formData.institution_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, institution_name: e.target.value }))}
                  style={styles.input}
                  placeholder="Ex: Ingressa"
                />
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                  Exibida no login, acima do ícone do sistema.
                </div>
              </div>
              <div>
                <label style={styles.label}>Nome do sistema</label>
                <input
                  type="text"
                  value={formData.system_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, system_name: e.target.value }))}
                  style={styles.input}
                  placeholder="Ex: Ingressa — Processos Seletivos"
                />
              </div>
              <div
                style={{
                  textAlign: 'center',
                  padding: '20px 16px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: 12,
                  border: '1px solid #E5E7EB',
                }}
              >
                <label style={{ ...styles.label, display: 'block', marginBottom: 8 }}>
                  Ícone do sistema (ex.: letra &quot;i&quot;)
                </label>
                <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 16px' }}>
                  PNG ou JPG. Aparece centralizado no login, logo abaixo da sigla.
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', minHeight: 100, marginBottom: 12 }}>
                  {(formData.institution_icon?.startsWith('http') || formData.institution_icon?.startsWith('/')) && !previewError.system ? (
                    <img
                      key={formData.institution_icon}
                      src={formData.institution_icon}
                      alt=""
                      style={{ width: 88, height: 88, objectFit: 'contain' }}
                      onError={() => setPreviewError((prev) => ({ ...prev, system: true }))}
                    />
                  ) : (
                    <>
                      <img src={DEFAULT_LOGO} alt="" style={{ width: 72, height: 72, objectFit: 'contain', opacity: 0.85 }} />
                      <span style={{ marginLeft: 12, fontSize: 13, color: '#9CA3AF', alignSelf: 'center' }}>
                        {previewError.system ? 'Erro na prévia.' : 'Padrão até enviar imagem'}
                      </span>
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  {renderImageActions(
                    'institution_icon',
                    'system',
                    (formData.institution_icon?.startsWith('http') || formData.institution_icon?.startsWith('/')) && !previewError.system
                  )}
                </div>
              </div>

              <div style={{ paddingTop: 8, marginTop: 16, borderTop: '1px solid #E5E7EB' }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: '#374151', margin: '0 0 16px' }}>
                  Dados cadastrais (aba Sobre)
                </h3>
              </div>
              <div>
                <label style={styles.label}>Razão social</label>
                <input
                  type="text"
                  value={formData.platform_owner_legal_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, platform_owner_legal_name: e.target.value }))}
                  style={styles.input}
                  placeholder="Nome jurídico da empresa proprietária"
                />
              </div>
              <div>
                <label style={styles.label}>CNPJ</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.platform_owner_cnpj}
                  onChange={(e) => setFormData((prev) => ({ ...prev, platform_owner_cnpj: maskCnpjInput(e.target.value) }))}
                  style={styles.input}
                  placeholder="00.000.000/0000-00"
                />
              </div>
              <div>
                <label style={styles.label}>Telefone</label>
                <input
                  type="text"
                  inputMode="tel"
                  value={formData.platform_owner_phone}
                  onChange={(e) => {
                    const masked = maskPhoneInput(e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      platform_owner_phone: masked,
                      ...(masked.replace(/\D/g, '').length === 0
                        ? { platform_owner_phone_whatsapp: false }
                        : {}),
                    }));
                  }}
                  style={styles.input}
                  placeholder="(00) 00000-0000"
                />
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginTop: 10,
                    fontSize: 14,
                    color: '#374151',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!formData.platform_owner_phone_whatsapp}
                    disabled={!formData.platform_owner_phone.replace(/\D/g, '')}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        platform_owner_phone_whatsapp: e.target.checked,
                      }))
                    }
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                  />
                  <WhatsAppIcon size={18} />
                  <span>Este número é WhatsApp (exibir ícone na aba Sobre)</span>
                </label>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Com DDD. Deixe em branco se não quiser exibir.
                </div>
              </div>
            </>
          )}
          {!isMaster && (
          <>
          <div>
            <label style={styles.label}>Sigla da instituição</label>
            <input
              type="text"
              value={formData.institution_name}
              onChange={(e) => setFormData(prev => ({ ...prev, institution_name: e.target.value }))}
              style={styles.input}
              placeholder="Ex: IFNMG, IFMG, etc."
            />
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
              Exibido no login, em linha separada
            </div>
          </div>
          <div>
            <label style={styles.label}>Nome da Instituição</label>
            <input
              type="text"
              value={formData.system_name}
              onChange={(e) => setFormData(prev => ({ ...prev, system_name: e.target.value }))}
              style={styles.input}
              placeholder="Ex: Instituto Federal do Norte de Minas Gerais"
            />
          </div>
            <div>
              <label style={styles.label}>Tempo máximo sem atividade (minutos)</label>
              <input
                type="number"
                min={SESSION_IDLE_MIN}
                max={SESSION_IDLE_MAX}
                step="1"
                value={formData.session_idle_timeout_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, session_idle_timeout_minutes: e.target.value }))}
                style={styles.input}
                placeholder={`Entre ${SESSION_IDLE_MIN} e ${SESSION_IDLE_MAX}`}
              />
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                Define em quantos minutos sem atividade o candidato será deslogado automaticamente.
              </div>
            </div>
          <div>
            <label style={styles.label}>Logomarca esquerda</label>
            <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>
              Envie uma imagem do seu computador (JPG, JPEG ou PNG)
            </p>
            {renderImageActions(
              'institution_icon',
              'left',
              (formData.institution_icon?.startsWith('http') || formData.institution_icon?.startsWith('/')) && !previewError.left
            )}
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
              Recomendado: imagem quadrada ou horizontal. Máximo permitido: 10MB. Aparece à esquerda no login e nos relatórios (impressão).
            </div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
              Prévia esquerda: {(formData.institution_icon?.startsWith('http') || formData.institution_icon?.startsWith('/')) && !previewError.left ? (
                <img
                  key={formData.institution_icon}
                  src={formData.institution_icon}
                  alt=""
                  style={{ width: 72, height: 72, objectFit: 'contain', verticalAlign: 'middle' }}
                  onError={() => setPreviewError(prev => ({ ...prev, left: true }))}
                />
              ) : (
                <>
                  <img src={DEFAULT_LOGO} alt="" style={{ width: 72, height: 72, objectFit: 'contain', verticalAlign: 'middle' }} />
                  <span style={{ marginLeft: '8px', color: '#6B7280' }}>
                    {previewError.left ? 'Erro ao carregar prévia da imagem.' : 'Logo padrão (public/ingressa-logo.svg)'}
                  </span>
                </>
              )}
            </div>
          </div>
          <div>
            <label style={styles.label}>Logomarca direita (opcional)</label>
            <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>
              Se enviada, será exibida à direita no login, nos relatórios (impressão) e no cabeçalho dos gabaritos
            </p>
            {renderImageActions(
              'institution_icon_right',
              'right',
              (formData.institution_icon_right?.startsWith('http') || formData.institution_icon_right?.startsWith('/')) && !previewError.right
            )}
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
              Recomendado: imagem quadrada ou horizontal. Máximo permitido: 10MB.
            </div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
              Prévia direita: {(formData.institution_icon_right?.startsWith('http') || formData.institution_icon_right?.startsWith('/')) && !previewError.right ? (
                <img
                  key={formData.institution_icon_right}
                  src={formData.institution_icon_right}
                  alt=""
                  style={{ width: 72, height: 72, objectFit: 'contain', verticalAlign: 'middle' }}
                  onError={() => setPreviewError(prev => ({ ...prev, right: true }))}
                />
              ) : (
                <span>{previewError.right ? 'Erro ao carregar prévia da imagem.' : 'Nenhuma (opcional)'}</span>
              )}
            </div>
          </div>
            <div>
              <label style={styles.label}>Termos de Uso</label>
              <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '8px' }}>
                Texto exibido no cadastro de candidatos. Escreva normalmente; quebras de linha serão preservadas.
              </p>
              <textarea
                value={formData.terms_of_use}
                onChange={(e) => setFormData(prev => ({ ...prev, terms_of_use: e.target.value }))}
                style={{ ...styles.input, minHeight: '200px', fontSize: '14px' }}
                placeholder="Termos de uso do sistema..."
              />
            </div>
          </>
          )}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
          <button
            type="submit"
            disabled={saving}
            style={{ ...styles.button, ...styles.buttonPrimary, opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
          {isMaster && typeof onSaved === 'function' && (
            <button
              type="button"
              disabled={saving}
              onClick={() => onSaved()}
              style={{ ...styles.button, ...styles.buttonSecondary }}
            >
              Voltar sem salvar
            </button>
          )}
          </div>
        </form>

        {!isMaster && (
          <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
            <h3 style={{ ...styles.cardTitle, fontSize: '18px', marginBottom: '8px' }}>📧 Conta de e-mail para envios automáticos</h3>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '12px', lineHeight: 1.55 }}>
              Configuração <strong>separada do login no Ingressa</strong>. Usada para link de cadastro de candidato e recuperação de senha.
              Prefira um endereço exclusivo (ex.: <code style={{ fontSize: 13 }}>noreply@</code> ou <code style={{ fontSize: 13 }}>processosseletivos@</code>).
            </p>
            {mailConfig.mail_configured && (
              <p style={{
                fontSize: '13px',
                color: '#166534',
                backgroundColor: '#F0FDF4',
                border: '1px solid #BBF7D0',
                borderRadius: '8px',
                padding: '10px 12px',
                marginBottom: '16px',
                lineHeight: 1.5,
              }}>
                Senha SMTP já configurada e armazenada com segurança. O campo abaixo fica vazio de propósito — preencha apenas se for trocar a senha.
              </p>
            )}
            <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={styles.label}>Servidor SMTP</label>
                <input
                  type="text"
                  name="smtp-mail-server"
                  autoComplete="off"
                  value={mailConfig.mail_server}
                  onChange={(e) => setMailConfig(prev => ({ ...prev, mail_server: e.target.value }))}
                  placeholder="Ex: smtp.gmail.com, smtp.office365.com"
                  style={styles.input}
                />
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px', marginBottom: 0 }}>
                  Nome do servidor de e-mail da instituição (não é um e-mail). Ex.: Gmail = smtp.gmail.com, Outlook = smtp.office365.com, servidor próprio = mail.instituicao.edu.br
                </p>
              </div>
              <div>
                <label style={styles.label}>Porta</label>
                <input
                  type="number"
                  name="smtp-mail-port"
                  autoComplete="off"
                  value={mailConfig.mail_port}
                  onChange={(e) => setMailConfig(prev => ({ ...prev, mail_port: parseInt(e.target.value, 10) || 587 }))}
                  placeholder="587"
                  style={{ ...styles.input, width: '80px' }}
                />
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px', marginBottom: 0 }}>
                  Porta do servidor de e-mail da instituição (não do servidor onde o sistema está). Geralmente 587 (TLS) ou 465 (SSL).
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '4px' }}>
                  <input
                    type="checkbox"
                    checked={mailConfig.mail_use_tls}
                    onChange={(e) => setMailConfig(prev => ({ ...prev, mail_use_tls: e.target.checked }))}
                  />
                  <span>Usar TLS</span>
                </label>
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px', marginBottom: 0 }}>
                  Mantenha marcado na maioria dos provedores. O suporte de TI da instituição pode confirmar.
                </p>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={styles.label}>Usuário / e-mail SMTP</label>
                <input
                  type="text"
                  name="smtp-mail-username"
                  autoComplete="off"
                  value={mailConfig.mail_username}
                  onChange={(e) => setMailConfig(prev => ({ ...prev, mail_username: e.target.value }))}
                  placeholder="seu-email@instituicao.edu.br"
                  style={styles.input}
                />
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px', marginBottom: 0 }}>
                  E-mail completo da conta que envia as mensagens no servidor SMTP — não é o seu login de administrador no Ingressa.
                </p>
              </div>
              <div>
                <label style={styles.label}>Senha SMTP</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showMailPassword ? 'text' : 'password'}
                    name="smtp-mail-password"
                    autoComplete="new-password"
                    value={mailConfig.mail_password}
                    onChange={(e) => setMailConfig(prev => ({ ...prev, mail_password: e.target.value }))}
                    placeholder={mailConfig.mail_configured ? 'Preencha só para trocar a senha gravada' : 'Senha de app ou senha SMTP'}
                    style={{ ...styles.input, paddingRight: '40px' }}
                  />
                  <EyeToggle visible={showMailPassword} onToggle={() => setShowMailPassword(v => !v)} />
                </div>
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px', marginBottom: 0 }}>
                  Senha de acesso ao servidor de e-mail — <strong>não</strong> é a senha do Ingressa.
                  No Gmail/Outlook, use a <strong>senha de app</strong> (16 caracteres).
                  {mailConfig.mail_configured
                    ? ' Deixe em branco ao salvar se não for alterar a senha já gravada.'
                    : ' Informe na primeira configuração.'}
                </p>
              </div>
              <div>
                <label style={styles.label}>Remetente</label>
                <input
                  type="text"
                  name="smtp-mail-sender"
                  autoComplete="off"
                  value={mailConfig.mail_default_sender}
                  onChange={(e) => setMailConfig(prev => ({ ...prev, mail_default_sender: e.target.value }))}
                  placeholder="Ex: noreply@instituicao.edu.br"
                  style={styles.input}
                />
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px', marginBottom: 0 }}>
                  Endereço que aparecerá no “De” do e-mail. Normalmente é o mesmo do usuário. Recomenda-se noreply@ ou processosseletivos@ para indicar envio automático.
                </p>
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={styles.label}>Mensagem de contato (rodapé dos e-mails)</label>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>
                Aparece no rodapé dos e-mails automáticos (ex.: recuperação de senha). Personalize com telefone ou e-mail da instituição, se desejar.
              </p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <textarea
                  value={mailConfig.mail_contact_message}
                  onChange={(e) => setMailConfig(prev => ({ ...prev, mail_contact_message: e.target.value }))}
                  style={{ ...styles.input, minHeight: '80px', flex: 1 }}
                  placeholder={mailConfig.mail_contact_message_default}
                />
                <button
                  type="button"
                  onClick={() => setMailConfig(prev => ({ ...prev, mail_contact_message: prev.mail_contact_message_default || '' }))}
                  style={{ ...styles.button, ...styles.buttonSecondary, flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  Restaurar padrão
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={handleSaveMailConfig}
              disabled={mailConfigSaving}
              style={{ ...styles.button, ...styles.buttonPrimary, opacity: mailConfigSaving ? 0.7 : 1 }}
            >
              {mailConfigSaving ? 'Salvando...' : 'Salvar configurações de e-mail'}
            </button>
            </form>
          </div>
        )}


        {!isMaster && (
          <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
            <h3 style={{ ...styles.cardTitle, fontSize: '18px', marginBottom: '8px' }}>Integração CadÚnico (MDS)</h3>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
              Consulta à API CadÚnico via Conecta Gov (SERPRO/MDS). Credenciais por instituição.
              Sem credenciais, use o modo stub para testes locais.
            </p>
            <div style={{ marginBottom: '12px' }}>
              <label style={styles.label}>
                <input
                  type="checkbox"
                  checked={Boolean(cadunicoConfig.cadunico_enabled)}
                  onChange={(e) => setCadunicoConfig(prev => ({ ...prev, cadunico_enabled: e.target.checked }))}
                  style={{ marginRight: '8px' }}
                />
                Habilitar CadÚnico nesta instituição
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={styles.label}>Client ID</label>
                <input
                  type="text"
                  autoComplete="off"
                  value={cadunicoConfig.cadunico_client_id}
                  onChange={(e) => setCadunicoConfig(prev => ({ ...prev, cadunico_client_id: e.target.value }))}
                  placeholder="Client ID Conecta"
                  style={styles.input}
                />
              </div>
              <div>
                <label style={styles.label}>Client Secret</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showCadunicoSecret ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={cadunicoConfig.cadunico_client_secret}
                    onChange={(e) => setCadunicoConfig(prev => ({ ...prev, cadunico_client_secret: e.target.value }))}
                    placeholder={cadunicoConfig.cadunico_configured ? 'Preencha só para trocar o secret gravado' : 'Client secret'}
                    style={{ ...styles.input, paddingRight: '40px' }}
                  />
                  <EyeToggle visible={showCadunicoSecret} onToggle={() => setShowCadunicoSecret(v => !v)} />
                </div>
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px', marginBottom: 0 }}>
                  {cadunicoConfig.cadunico_configured
                    ? 'Deixe em branco ao salvar se não for alterar o secret já gravado.'
                    : 'Informe na primeira configuração (nunca exibido após salvar).'}
                </p>
              </div>
              <div>
                <label style={styles.label}>Ambiente</label>
                <select
                  value={cadunicoConfig.cadunico_environment || 'homolog'}
                  onChange={(e) => setCadunicoConfig(prev => ({ ...prev, cadunico_environment: e.target.value }))}
                  style={styles.input}
                >
                  <option value="homolog">Homologação</option>
                  <option value="prod">Produção</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={styles.label}>
                <input
                  type="checkbox"
                  checked={Boolean(cadunicoConfig.cadunico_use_stub)}
                  onChange={(e) => setCadunicoConfig(prev => ({ ...prev, cadunico_use_stub: e.target.checked }))}
                  style={{ marginRight: '8px' }}
                />
                Usar resposta stub (somente testes locais)
              </label>
              <p style={{ fontSize: '12px', color: '#B45309', marginTop: '4px', marginBottom: 0 }}>
                Atenção: no stub, a soma dos dígitos do NIS define o resultado (par = baixa renda). Não usar em produção.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowCadunicoAdvanced(v => !v)}
              style={{ ...styles.button, ...styles.buttonSecondary, marginBottom: '12px' }}
            >
              {showCadunicoAdvanced ? 'Ocultar URLs avançadas' : 'URLs avançadas (opcional)'}
            </button>
            {showCadunicoAdvanced && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={styles.label}>Token URL (override)</label>
                  <input
                    type="text"
                    value={cadunicoConfig.cadunico_token_url}
                    onChange={(e) => setCadunicoConfig(prev => ({ ...prev, cadunico_token_url: e.target.value }))}
                    placeholder="Vazio = URL oficial Conecta do ambiente"
                    style={styles.input}
                  />
                </div>
                <div>
                  <label style={styles.label}>API URL (override)</label>
                  <input
                    type="text"
                    value={cadunicoConfig.cadunico_api_url}
                    onChange={(e) => setCadunicoConfig(prev => ({ ...prev, cadunico_api_url: e.target.value }))}
                    placeholder="Vazio = URL oficial CadÚnico do ambiente"
                    style={styles.input}
                  />
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={handleSaveCadunicoConfig}
              disabled={cadunicoSaving}
              style={{ ...styles.button, ...styles.buttonPrimary, opacity: cadunicoSaving ? 0.7 : 1 }}
            >
              {cadunicoSaving ? 'Salvando...' : 'Salvar CadÚnico'}
            </button>
          </div>
        )}

        {!isMaster && (
          <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
            <h3 style={{ ...styles.cardTitle, fontSize: '18px', marginBottom: '8px' }}>📦 Backup e Segurança</h3>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
              Gere um backup dos dados da sua instituição. O arquivo é salvo no servidor e baixado no seu PC. Backup automático pode ser agendado.
            </p>

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '24px' }}>
              <button
                type="button"
                onClick={handleGenerateBackup}
                disabled={backupLoading}
                style={{
                  ...styles.button,
                  backgroundColor: '#059669',
                  color: 'white',
                  opacity: backupLoading ? 0.7 : 1
                }}
              >
                {backupLoading ? '⏳ Gerando backup...' : '📥 Gerar backup agora'}
              </button>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ ...styles.label, fontSize: '12px', marginBottom: '4px' }}>Manter últimos</label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={backupConfig.backup_manter}
                    onChange={(e) => setBackupConfig(prev => ({ ...prev, backup_manter: parseInt(e.target.value, 10) || 10 }))}
                    style={{ ...styles.input, width: '70px', padding: '6px 8px' }}
                  />
                </div>
                <div>
                  <label style={{ ...styles.label, fontSize: '12px', marginBottom: '4px' }}>Backup automático</label>
                  <select
                    value={backupConfig.backup_frequencia}
                    onChange={(e) => setBackupConfig(prev => ({ ...prev, backup_frequencia: e.target.value }))}
                    style={{ ...styles.select, minWidth: '140px', padding: '6px 10px' }}
                  >
                    <option value="">Nenhum</option>
                    <option value="diario">Diário (02:00)</option>
                    <option value="semanal">Semanal (domingo 02:00)</option>
                  </select>
                </div>
                <button
                  type="button"
                  onClick={handleSaveBackupConfig}
                  disabled={backupConfigSaving}
                  style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 12px' }}
                >
                  {backupConfigSaving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>

            {backupList.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Backups no servidor</h4>
                <div style={{ overflowX: 'auto', maxHeight: '200px', overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                  <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Arquivo</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Tamanho</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Data</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center' }}>Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backupList.map((b, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                          <td style={{ padding: '8px 12px' }}>{b.filename}</td>
                          <td style={{ padding: '8px 12px' }}>{formatBytes(b.size)}</td>
                          <td style={{ padding: '8px 12px' }}>{formatDate(b.modified)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const res = await fetch(apiClient.downloadBackup(b.filename), { credentials: 'include' });
                                  if (!res.ok) throw new Error('Erro ao baixar');
                                  const blob = await res.blob();
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = b.filename;
                                  a.click();
                                  URL.revokeObjectURL(url);
                                  addNotification('Sucesso', 'Backup baixado.', 'success');
                                } catch (e) {
                                  addNotification('Erro', e.message || 'Erro ao baixar', 'error');
                                }
                              }}
                              style={{ ...styles.button, ...styles.buttonSecondary, padding: '4px 8px', fontSize: '12px' }}
                            >
                              📥 Baixar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div style={{ padding: '16px', backgroundColor: '#FEF3C7', borderRadius: '8px', border: '1px solid #F59E0B' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#92400E', marginBottom: '8px' }}>Restaurar backup</h4>
              <p style={{ fontSize: '13px', color: '#92400E', marginBottom: '12px' }}>
                Envie o arquivo JSON de backup. Isso substituirá todos os dados atuais da instituição. Usuários receberão senha temporária (use "Esqueci minha senha").
              </p>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  id="restore-file-input"
                  type="file"
                  accept=".json,application/json"
                  onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                  style={{ fontSize: '13px' }}
                />
                <button
                  type="button"
                  onClick={handleRestoreBackup}
                  disabled={restoreLoading || !restoreFile}
                  style={{
                    ...styles.button,
                    backgroundColor: restoreFile ? '#DC2626' : '#9CA3AF',
                    color: 'white',
                    opacity: restoreLoading || !restoreFile ? 0.7 : 1
                  }}
                >
                  {restoreLoading ? '⏳ Restaurando...' : '📤 Restaurar backup'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
